import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiEdit3,
  FiTrash2,
  FiChevronDown,
  FiClock,
  FiActivity,
} from 'react-icons/fi';
import MedicineService from '../../services/medicineService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';
import { API_DOCTOR_LIST } from "../../../../../helpers/urlHelper";
import { getMedicines } from '../../../../MedicineComponent/api'; // Ajusta la ruta según tu estructura

const MedicineTreatment = ({ onCountUpdate, componentName = 'MedicineTreatment' }) => {
  const location = useLocation();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;
  const [availableDoctors, setAvailableDoctors] = useState([]);
const [availableMedicines, setAvailableMedicines] = useState([]);
const [doctorsLoading, setDoctorsLoading] = useState(false);
const [medicinesLoading, setMedicinesLoading] = useState(false);

  const [medicines, setMedicines] = useState([]);
  const [filteredMedicines, setFilteredMedicines] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  
  // Estado para Add New offcanvas
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: 'All',
    isActive: '',
    isExpired: '',
    prescribedBy: '',
    startDate: '',
    endDate: '',
    duration: ''
  });



  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS**
  const loadMedicines = useCallback(async () => {
    if (!patientId) {
      console.log('No patientId provided, component will show empty state');
      setMedicines([]);
      onCountUpdate && onCountUpdate(componentName, 0);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Loading medicines for patient:', patientId);
      const response = await MedicineService.getMedicinesByUser(patientId);
      console.log('Raw API response:', response);
      
      let medicinesData = [];
      
      if (response.data) {
        medicinesData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        medicinesData = response;
      } else if (response.medicines) {
        medicinesData = Array.isArray(response.medicines) ? response.medicines : [response.medicines];
      }

      const normalizedMedicines = medicinesData.map(medicine => {
        console.log('Processing medicine:', medicine);
        
        // Handle patient information (could be populated or just ID)
        const patientInfo = typeof medicine.assignto === 'object' && medicine.assignto 
          ? { id: medicine.assignto._id, name: medicine.assignto.name }
          : { id: medicine.assignto || patientId, name: patientData.patientName || 'Unknown Patient' };

        // Handle prescribedBy information (could be populated or just ID)
        const prescribedByInfo = typeof medicine.prescribedby === 'object' && medicine.prescribedby
          ? { id: medicine.prescribedby._id, name: medicine.prescribedby.name, email: medicine.prescribedby.email }
          : { id: medicine.prescribedby, name: 'Unknown', email: '' };

        // Handle referredBy information (could be populated or just ID)
        const referredByInfo = typeof medicine.referredby === 'object' && medicine.referredby
          ? { id: medicine.referredby._id, name: medicine.referredby.name, email: medicine.referredby.email }
          : { id: medicine.referredby, name: 'Unknown', email: '' };

        // Calculate if medicine is active/expired
        const now = new Date();
        const startDate = medicine.startdate ? new Date(medicine.startdate) : null;
        const endDate = medicine.enddate ? new Date(medicine.enddate) : null;
        
        const isActive = !endDate || (endDate >= now && (!startDate || startDate <= now));
        const isExpired = endDate && endDate < now;
        const isUpcoming = startDate && startDate > now;

        return {
          // Primary identifiers
          medicine_id: medicine._id || medicine.medicine_id || medicine.id,
          
          // Medicine information
          name: medicine.name || '',
          duration: medicine.duration || 0,
          start_date: medicine.startdate || null,
          end_date: medicine.enddate || null,
          
          // Usage information
          usages: medicine.usages || [],
          usage_days_of_week: medicine.usagedaysofweek || [],
          usage_dates: medicine.usagedates || [],
          
          // Additional data
          comments: medicine.comments || '',
          status: medicine.status || '1',
          
          // Personnel
          prescribed_by: prescribedByInfo.name,
          prescribed_by_id: prescribedByInfo.id,
          prescribed_by_email: prescribedByInfo.email,
          referred_by: referredByInfo.name,
          referred_by_id: referredByInfo.id,
          referred_by_email: referredByInfo.email,
          
          // Patient info
          patient_id: patientInfo.id,
          patient_name: patientInfo.name,
          
          // Computed fields
          is_active: isActive,
          is_expired: isExpired,
          is_upcoming: isUpcoming,
          
          // Dates
          created_date: medicine.createdAt || medicine.created_date,
          updated_date: medicine.updatedAt || medicine.updated_date,
          
          // Preserve original for detailed view
          _original: medicine
        };
      });

      console.log('Normalized medicines:', normalizedMedicines);
      
      // **IMPORTANTE: Solo actualizar si el contador cambió**
      if (medicines.length !== normalizedMedicines.length) {
        console.log(`📊 ${componentName}: Contador cambió de ${medicines.length} a ${normalizedMedicines.length}`);
        onCountUpdate && onCountUpdate(componentName, normalizedMedicines.length);
      }
      
      setMedicines(normalizedMedicines);
    } catch (error) {
      console.error('Error loading medicines:', error);
      setMedicines([]);
      // También actualizar contador en caso de error
      if (medicines.length !== 0) {
        onCountUpdate && onCountUpdate(componentName, 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, medicines.length, onCountUpdate, componentName, patientData]);

  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadMedicinesStats = useCallback(async () => {
    if (!patientId || medicines.length === 0) return;
    
    try {
      const activeCount = medicines.filter(medicine => 
        medicine.is_active && medicine.status === '1'
      ).length;
      
      const expiredCount = medicines.filter(medicine => 
        medicine.is_expired
      ).length;
      
      const upcomingCount = medicines.filter(medicine => 
        medicine.is_upcoming
      ).length;
      
      if (activeCount > 0 || expiredCount > 0 || upcomingCount > 0) {
        let message = `${activeCount} active medicines`;
        if (expiredCount > 0) message += `, ${expiredCount} expired`;
        if (upcomingCount > 0) message += `, ${upcomingCount} upcoming`;
        
        setAlertInfo({
          alert_type: 'medicine',
          message: message,
          priority: expiredCount > 0 ? 'high' : activeCount > 0 ? 'medium' : 'low',
          requires_action: expiredCount > 0
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading medicines stats:', error);
      setAlertInfo(null);
    }
  }, [patientId, medicines]);

  const applyFilters = useCallback(() => {
    console.log('🔄 Aplicando filtros y ordenamiento...');
    console.log('Medicines array length:', medicines.length);
    console.log('Current sortBy:', sortBy);
    
    let filtered = [...medicines];

    // Aplicar filtro de búsqueda global
    if (globalFilter) {
      filtered = filtered.filter(medicine => 
        (medicine.name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (medicine.prescribed_by || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (medicine.comments || '').toLowerCase().includes(globalFilter.toLowerCase())
      );
    }

    // Aplicar filtros específicos
    if (filters.status !== 'All') {
      if (filters.status === 'Active') {
        filtered = filtered.filter(medicine => medicine.status === '1');
      } else if (filters.status === 'Inactive') {
        filtered = filtered.filter(medicine => medicine.status === '0');
      }
    }

    if (filters.isActive) {
      if (filters.isActive === 'true') {
        filtered = filtered.filter(medicine => medicine.is_active);
      } else if (filters.isActive === 'false') {
        filtered = filtered.filter(medicine => !medicine.is_active);
      }
    }

    if (filters.isExpired) {
      if (filters.isExpired === 'true') {
        filtered = filtered.filter(medicine => medicine.is_expired);
      } else if (filters.isExpired === 'false') {
        filtered = filtered.filter(medicine => !medicine.is_expired);
      }
    }

    if (filters.prescribedBy) {
      filtered = filtered.filter(medicine => 
        (medicine.prescribed_by || '').toLowerCase().includes(filters.prescribedBy.toLowerCase())
      );
    }

    if (filters.startDate) {
      filtered = filtered.filter(medicine => {
        const startDate = medicine.start_date;
        return startDate && new Date(startDate) >= new Date(filters.startDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(medicine => {
        const startDate = medicine.start_date;
        return startDate && new Date(startDate) <= new Date(filters.endDate);
      });
    }

    if (filters.duration) {
      filtered = filtered.filter(medicine => {
        const duration = medicine.duration || 0;
        return duration >= parseInt(filters.duration);
      });
    }

    console.log('Filtered array length:', filtered.length);

    // Ordenamiento mejorado
    filtered.sort((a, b) => {
      console.log('Sorting with:', sortBy);
      
      const getDate = (item) => {
        const dateValue = item.start_date || item.created_date || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'alphabetical':
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        case 'duration':
          return (b.duration || 0) - (a.duration || 0);
        case 'status':
          if (a.is_active !== b.is_active) {
            return a.is_active ? -1 : 1;
          }
          return 0;
        default:
          return 0;
      }
    });

    console.log('Final filtered array length:', filtered.length);
    setFilteredMedicines(filtered);
  }, [medicines, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'alphabetical': return 'A-Z';
      case 'duration': return 'By Duration';
      case 'status': return 'By Status';
      default: return 'Sort by';
    }
  };

  // Table columns definition
  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Medicine Name',
      size: 200,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <FiActivity className="text-primary me-2" />
          <div>
            <div className="fw-bold text-primary">
              {row.original.name || 'Unknown Medicine'}
            </div>
            <small className="text-muted">
              Duration: {row.original.duration} days
            </small>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'prescribed_by',
      header: 'Prescribed By',
      size: 150,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.prescribed_by || 'Unknown'}</div>
          <small className="text-muted">
            {formatDate(row.original.start_date)}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'start_date',
      header: 'Start Date',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div>{formatDate(row.original.start_date)}</div>
          {row.original.end_date && (
            <small className="text-muted">
              End: {formatDate(row.original.end_date)}
            </small>
          )}
        </div>
      )
    },
    {
      accessorKey: 'duration',
      header: 'Duration',
      size: 100,
      Cell: ({ row }) => (
        <div className="text-center">
          <span className="badge bg-info">
            {row.original.duration} days
          </span>
        </div>
      )
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex flex-column gap-1">
          <span className={`badge bg-${getMedicineStatusColor(row.original)}`}>
            {getMedicineStatusText(row.original)}
          </span>
          {row.original.is_expired && (
            <span className="badge bg-danger">
              <FiAlertTriangle size={12} className="me-1" />
              Expired
            </span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'usage_days_of_week',
      header: 'Schedule',
      size: 150,
      Cell: ({ row }) => (
        <div>
          {row.original.usage_days_of_week?.length > 0 ? (
            <div className="d-flex flex-wrap gap-1">
              {row.original.usage_days_of_week.map((day, index) => (
                <span key={index} className="badge bg-secondary">
                  {getDayName(day)}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-muted">No schedule</span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Record Status',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.status)}`}>
          {getStatusText(row.original.status)}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewMedicine(row.original.medicine_id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEditMedicine(row.original.medicine_id);
            }}
            title="Edit"
          >
            <FiEdit3 />
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteMedicine(row.original.medicine_id);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ], [medicines]);

  // **EFECTO PRINCIPAL PARA CARGAR DATOS**
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadMedicines();
    }
  }, [patientId, loadMedicines]);

  useEffect(() => {
  fetchDoctors();
  fetchAvailableMedicines();
}, []);

  // **EFECTO SEPARADO PARA ESTADÍSTICAS**
  useEffect(() => {
    if (medicines.length > 0) {
      loadMedicinesStats();
    }
  }, [medicines, loadMedicinesStats]);

  // **EFECTO PARA ACTUALIZAR CONTADOR CUANDO CAMBIEN LOS DATOS**
  useEffect(() => {
    console.log(`📊 ${componentName}: Actualizando contador a ${medicines.length}`);
    onCountUpdate && onCountUpdate(componentName, medicines.length);
  }, [medicines.length, onCountUpdate, componentName]);

  // Apply filters whenever medicines or filters change
  useEffect(() => {
    console.log('📊 useEffect applyFilters triggered');
    console.log('- medicines.length:', medicines.length);
    console.log('- sortBy:', sortBy);
    
    if (medicines.length > 0) {
      applyFilters();
    } else {
      console.log('⚠️ No hay medicines, limpiando filteredMedicines');
      setFilteredMedicines([]);
    }
  }, [medicines, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas, showEditOffcanvas]);

  // Loading state
  if (isLoading && patientId) {
    return <div>Loading patient data...</div>;
  }

  const handleViewMedicine = (medicineId) => {
    console.log('Viewing medicine:', medicineId);
    const medicine = medicines.find(m => m.medicine_id === medicineId);
    if (medicine) {
      console.log('Found medicine for viewing:', medicine);
      setSelectedMedicine(medicine);
      setShowViewOffcanvas(true);
    } else {
      console.error('Medicine not found:', medicineId);
    }
  };
  
  const handleEditMedicine = (medicineId) => {
    console.log('Editing medicine:', medicineId);
    const medicine = medicines.find(m => m.medicine_id === medicineId);
    if (medicine) {
      console.log('Found medicine for editing:', medicine);
      setEditingMedicine(medicine);
      setShowEditOffcanvas(true);
    } else {
      console.error('Medicine not found for editing:', medicineId);
    }
  };

  const handleAddNew = () => {
    console.log('Add New button clicked - opening modal');
    setShowFilterOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowAddOffcanvas(true);
  };

  const handleSubmitNewMedicine = async (medicineData) => {
    console.log('=== SUBMITTING NEW MEDICINE ===');
    console.log('Original medicine data:', medicineData);
    
    setIsSubmitting(true);
    
    try {
      const currentUser = getCurrentUser();
      const currentEcosystem = getCurrentEcosystem();
      
      console.log('Current user:', currentUser);
      console.log('Current ecosystem:', currentEcosystem);
      
      if (!currentUser || !currentUser.id) {
        throw new Error('No se pudo obtener información del usuario actual. Por favor, inicia sesión nuevamente.');
      }
      
      if (!currentEcosystem || !currentEcosystem.id) {
        throw new Error('No se pudo obtener información del ecosistema actual. Por favor, verifica tu configuración.');
      }

      if (!patientId) {
        throw new Error('No se pudo obtener el ID del paciente.');
      }

      const completeMedicineData = {
        assignto: patientId,
        prescribedby: medicineData.prescribedby || currentUser.id,
        referredby: medicineData.referredby || null,
        name: medicineData.name || '',
        duration: parseInt(medicineData.duration) || 0,
        startdate: medicineData.startdate || '',
        enddate: medicineData.enddate || '',
        usages: medicineData.usages || [],
        usagedaysofweek: medicineData.usagedaysofweek || [],
        usagedates: medicineData.usagedates || [],
        comments: medicineData.comments || '',
        ecosystemid: currentEcosystem.id,
        status: '1'
      };

      console.log('Complete medicine data to send:', completeMedicineData);
      
      const result = await MedicineService.createMedicine(completeMedicineData);
      console.log('Medicine created successfully:', result);
      
      await loadMedicines();
      await loadMedicinesStats();
      
      setShowAddOffcanvas(false);
      
      if (window.showSuccessMessage) {
        window.showSuccessMessage('Medicine created successfully!');
      } else {
        alert('Medicine created successfully!');
      }
      
    } catch (error) {
      console.error('Error creating medicine:', error);
      
      let errorMessage = 'Error creating medicine. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      alert(`Error: ${errorMessage}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateMedicine = async (updatedData) => {
    console.log('=== UPDATING MEDICINE ===');
    console.log('Updated medicine data:', updatedData);
    console.log('Editing medicine ID:', editingMedicine?.medicine_id);
    
    setIsSubmitting(true);
    
    try {
      if (!editingMedicine?.medicine_id) {
        throw new Error('No se pudo obtener el ID de la medicina a actualizar');
      }

      const updateData = {
        name: updatedData.name || '',
        duration: parseInt(updatedData.duration) || 0,
        startdate: updatedData.startdate || '',
        enddate: updatedData.enddate || '',
        usages: updatedData.usages || [],
        usagedaysofweek: updatedData.usagedaysofweek || [],
        usagedates: updatedData.usagedates || [],
        comments: updatedData.comments || '',
        prescribedby: updatedData.prescribedby || null,
        referredby: updatedData.referredby || null
      };

      console.log('Update data to send:', updateData);
      
      const result = await MedicineService.updateMedicine(editingMedicine.medicine_id, updateData);
      console.log('Medicine updated successfully:', result);
      
      await loadMedicines();
      await loadMedicinesStats();
      
      setShowEditOffcanvas(false);
      setEditingMedicine(null);
      
      if (window.showSuccessMessage) {
        window.showSuccessMessage('Medicine updated successfully!');
      } else {
        alert('Medicine updated successfully!');
      }
      
    } catch (error) {
      console.error('Error updating medicine:', error);
      
      let errorMessage = 'Error updating medicine. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      alert(`Error: ${errorMessage}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentUser = () => {
    console.log('Getting current user...');
    
    try {
      const userData = localStorage.getItem('currentUser') || localStorage.getItem('user') || localStorage.getItem('authUser');
      if (userData) {
        const user = JSON.parse(userData);
        console.log('User found in localStorage:', user);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.username || user.fullName,
          email: user.email
        };
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }

    try {
      const sessionUserData = sessionStorage.getItem('currentUser') || sessionStorage.getItem('user');
      if (sessionUserData) {
        const user = JSON.parse(sessionUserData);
        console.log('User found in sessionStorage:', user);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.username || user.fullName,
          email: user.email
        };
      }
    } catch (error) {
      console.error('Error parsing user data from sessionStorage:', error);
    }

    console.warn('No user data found, using default values for testing');
    return {
      id: '507f1f77bcf86cd799439011',
      name: 'Test User',
      email: 'test@example.com'
    };
  };

  const getCurrentEcosystem = () => {
    console.log('Getting current ecosystem...');
    
    try {
      const ecosystemData = localStorage.getItem('currentEcosystem') || localStorage.getItem('ecosystem');
      if (ecosystemData) {
        const ecosystem = JSON.parse(ecosystemData);
        console.log('Ecosystem found in localStorage:', ecosystem);
        return {
          id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
          name: ecosystem.name || ecosystem.ecosystemName
        };
      }
    } catch (error) {
      console.error('Error parsing ecosystem data from localStorage:', error);
    }

    try {
      const sessionEcosystemData = sessionStorage.getItem('currentEcosystem') || sessionStorage.getItem('ecosystem');
      if (sessionEcosystemData) {
        const ecosystem = JSON.parse(sessionEcosystemData);
        console.log('Ecosystem found in sessionStorage:', ecosystem);
        return {
          id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
          name: ecosystem.name || ecosystem.ecosystemName
        };
      }
    } catch (error) {
      console.error('Error parsing ecosystem data from sessionStorage:', error);
    }

    console.warn('No ecosystem data found, using default values for testing');
    return {
      id: '507f1f77bcf86cd799439012',
      name: 'Test Ecosystem'
    };
  };

  // 3. AGREGAR FUNCIÓN PARA CARGAR DOCTORES (después de getCurrentEcosystem, línea ~500)
const fetchDoctors = async () => {
  setDoctorsLoading(true);
  try {
    const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
    if (!doctorsResponse.ok) {
      throw new Error('Failed to fetch doctors');
    }
    const doctorsData = await doctorsResponse.json();
    console.log('Raw doctors response:', doctorsData);
    
    const doctorsArray = doctorsData && doctorsData.data ? doctorsData.data : [];
    console.log('Processed doctors array:', doctorsArray);
    
    // Formatear doctores para el selector
    const formattedDoctors = doctorsArray.map(doctor => ({
      id: doctor._id || doctor.id,
      name: `${doctor.name || ''} ${doctor.surname || ''}`.trim(),
      email: doctor.email || '',
      fullData: doctor
    }));
    
    setAvailableDoctors(formattedDoctors);
  } catch (err) {
    console.error('Error fetching doctors:', err);
    setAvailableDoctors([]);
  } finally {
    setDoctorsLoading(false);
  }
};

// 4. AGREGAR FUNCIÓN PARA CARGAR MEDICINAS (después de fetchDoctors)
const fetchAvailableMedicines = async () => {
  setMedicinesLoading(true);
  try {
    const medicinesData = await getMedicines();
    console.log('Available medicines:', medicinesData);
    
    // Formatear medicinas para el selector
    const formattedMedicines = medicinesData.map(medicine => ({
      id: medicine._id || medicine.id,
      name: medicine.name || 'Unknown Medicine',
      category: medicine.category || '',
      description: medicine.description || '',
      fullData: medicine
    }));
    
    setAvailableMedicines(formattedMedicines);
  } catch (err) {
    console.error('Error fetching available medicines:', err);
    setAvailableMedicines([]);
  } finally {
    setMedicinesLoading(false);
  }
};



  const handleDeleteMedicine = async (medicineId) => {
    if (window.confirm('Are you sure you want to delete this medicine?')) {
      try {
        await MedicineService.deleteMedicine(medicineId);
        await loadMedicines();
        await loadMedicinesStats();
      } catch (error) {
        console.error('Error deleting medicine:', error);
        alert('Error deleting medicine. Please try again.');
      }
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'All',
      isActive: '',
      isExpired: '',
      prescribedBy: '',
      startDate: '',
      endDate: '',
      duration: ''
    });
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedMedicine(null);
  };

  const handleCloseAddOffcanvas = () => {
    console.log('Closing add offcanvas');
    if (!isSubmitting) {
      setShowAddOffcanvas(false);
    }
  };

  const handleCloseEditOffcanvas = () => {
    console.log('Closing edit offcanvas');
    if (!isSubmitting) {
      setShowEditOffcanvas(false);
      setEditingMedicine(null);
    }
  };

  const handleCloseFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  const handleOpenFilterOffcanvas = () => {
    setShowAddOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowFilterOffcanvas(true);
  };

  const getMedicineStatusColor = (medicine) => {
    if (medicine.is_expired) return 'danger';
    if (medicine.is_active) return 'success';
    if (medicine.is_upcoming) return 'warning';
    return 'secondary';
  };

  const getMedicineStatusText = (medicine) => {
    if (medicine.is_expired) return 'Expired';
    if (medicine.is_active) return 'Active';
    if (medicine.is_upcoming) return 'Upcoming';
    return 'Inactive';
  };

  const getStatusColor = (status) => {
    return status === '1' ? 'success' : 'secondary';
  };

  const getStatusText = (status) => {
    return status === '1' ? 'Active' : 'Inactive';
  };

  const getDayName = (day) => {
    const days = {
      '1': 'Mon',
      '2': 'Tue',
      '3': 'Wed',
      '4': 'Thu',
      '5': 'Fri',
      '6': 'Sat',
      '7': 'Sun'
    };
    return days[day] || day;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({
        key: 'search',
        label: `Search: "${globalFilter}"`,
        value: globalFilter,
        type: 'search'
      });
    }
    
    if (filters.status !== 'All') {
      activeFilters.push({
        key: 'status',
        label: `Status: ${filters.status}`,
        value: filters.status,
        type: 'status'
      });
    }
    
    if (filters.isActive) {
      activeFilters.push({
        key: 'isActive',
        label: `Active: ${filters.isActive === 'true' ? 'Yes' : 'No'}`,
        value: filters.isActive,
        type: 'isActive'
      });
    }
    
    if (filters.isExpired) {
      activeFilters.push({
        key: 'isExpired',
        label: `Expired: ${filters.isExpired === 'true' ? 'Yes' : 'No'}`,
        value: filters.isExpired,
        type: 'isExpired'
      });
    }
    
    if (filters.prescribedBy) {
      activeFilters.push({
        key: 'prescribedBy',
        label: `Prescribed By: ${filters.prescribedBy}`,
        value: filters.prescribedBy,
        type: 'prescribedBy'
      });
    }
    
    if (filters.startDate) {
      activeFilters.push({
        key: 'startDate',
        label: `From: ${formatDate(filters.startDate)}`,
        value: filters.startDate,
        type: 'startDate'
      });
    }
    
    if (filters.endDate) {
      activeFilters.push({
        key: 'endDate',
        label: `To: ${formatDate(filters.endDate)}`,
        value: filters.endDate,
        type: 'endDate'
      });
    }
    
    if (filters.duration) {
      activeFilters.push({
        key: 'duration',
        label: `Min Duration: ${filters.duration} days`,
        value: filters.duration,
        type: 'duration'
      });
    }
    
    if (sortBy !== 'newest') {
      activeFilters.push({
        key: 'sort',
        label: `Sort: ${getSortDisplayText()}`,
        value: sortBy,
        type: 'sort'
      });
    }
    
    return activeFilters;
  };

  const handleRemoveFilter = (filterToRemove) => {
    switch (filterToRemove.type) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'status':
        setFilters(prev => ({ ...prev, status: 'All' }));
        break;
      case 'isActive':
        setFilters(prev => ({ ...prev, isActive: '' }));
        break;
      case 'isExpired':
        setFilters(prev => ({ ...prev, isExpired: '' }));
        break;
      case 'prescribedBy':
        setFilters(prev => ({ ...prev, prescribedBy: '' }));
        break;
      case 'startDate':
        setFilters(prev => ({ ...prev, startDate: '' }));
        break;
      case 'endDate':
        setFilters(prev => ({ ...prev, endDate: '' }));
        break;
      case 'duration':
        setFilters(prev => ({ ...prev, duration: '' }));
        break;
      case 'sort':
        setSortBy('newest');
        break;
      default:
        break;
    }
  };

  const handleClearAllActiveFilters = () => {
    setGlobalFilter('');
    setFilters({
      status: 'All',
      isActive: '',
      isExpired: '',
      prescribedBy: '',
      startDate: '',
      endDate: '',
      duration: ''
    });
    setSortBy('newest');
  };

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid w-100">
        <PatientManagement activeMenu='/my-medicines'/>
        
        {/* Breadcrumb */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                My Medications {patientData?.patientName ? `- ${patientData.patientName}` : ''}
              </li>
            </ol>
          </nav>
        </div>

        <div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search medicines..."
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Right side - Controls */}
                <div className="d-flex align-items-center gap-2">
                  {/* Sort By Dropdown */}
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSortDropdown(!showSortDropdown);
                      }}
                    >
                      {getSortDisplayText()}
                      <FiChevronDown />
                    </button>
                    {showSortDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                        <button
                          className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('newest')}
                        >
                          Newest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('oldest')}
                        >
                          Oldest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                          onClick={() => handleSortChange('alphabetical')}
                        >
                          A-Z
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'duration' ? 'active' : ''}`}
                          onClick={() => handleSortChange('duration')}
                        >
                          By Duration
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'status' ? 'active' : ''}`}
                          onClick={() => handleSortChange('status')}
                        >
                          By Status
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Filter Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handleOpenFilterOffcanvas}
                  >
                    <FiFilter />
                    Filter
                  </button>
            
                  {/* Add New Button */}
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={handleAddNew}
                  >
                    <FiPlus />
                    Add New
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFilters().length > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                  <span className="text-muted fw-medium">Active filters:</span>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {getActiveFilters().map((filter) => (
                      <span
                        key={filter.key}
                        className="badge bg-primary d-flex align-items-center gap-1"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                      >
                        {filter.label}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ 
                            fontSize: '0.625rem',
                            padding: '0.125rem',
                            marginLeft: '0.25rem'
                          }}
                          onClick={() => handleRemoveFilter(filter)}
                          title={`Remove ${filter.label} filter`}
                        ></button>
                      </span>
                    ))}
                    
                    {/* Clear All Button */}
                    {getActiveFilters().length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={handleClearAllActiveFilters}
                        title="Clear all filters"
                      >
                        <FiX size={14} />
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Table */}
          <MaterialReactTable
            columns={columns}
            data={filteredMedicines}
            
            // Configuración básica
            enableRowSelection
            enableColumnFilters
            enableGlobalFiltering
            enableSorting={false}
            enablePagination
            
            // Toolbars
            enableTopToolbar
            enableBottomToolbar
            
            // Features opcionales
            enableDensityToggle
            enableFullScreenToggle
            enableHiding
            enableColumnOrdering
            enableColumnResizing={false}
            enableGrouping={false}
            
            // Estados
            initialState={{
              pagination: { pageSize: 10, pageIndex: 0 },
              sorting: [{ id: 'name', desc: false }],
              density: 'comfortable',
              showGlobalFilter: false
            }}
            
            state={{
              isLoading,
              rowSelection,
              globalFilter
            }}
            
            // Callbacks
            onRowSelectionChange={setRowSelection}
            onGlobalFilterChange={handleGlobalFilterChange}
            
            // Configuración de renderizado
            muiTableContainerProps={{
              sx: { maxHeight: '600px' }
            }}
          />

          {/* Results Summary */}
          {filteredMedicines.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredMedicines.length} of {medicines.length} medicines
                {Object.values(filters).some(f => f && f !== 'All') || globalFilter ? ' (filtered)' : ''}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseFilterOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1">
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Medicines</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseFilterOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                {/* Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Record Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                {/* Is Active Filter */}
                <div className="mb-3">
                  <label className="form-label">Currently Active</label>
                  <select
                    className="form-select"
                    value={filters.isActive}
                    onChange={(e) => handleFilterChange('isActive', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Not Active</option>
                  </select>
                </div>

                {/* Is Expired Filter */}
                <div className="mb-3">
                  <label className="form-label">Expired Status</label>
                  <select
                    className="form-select"
                    value={filters.isExpired}
                    onChange={(e) => handleFilterChange('isExpired', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="true">Expired</option>
                    <option value="false">Not Expired</option>
                  </select>
                </div>

                {/* Prescribed By Filter */}
                <div className="mb-3">
                  <label className="form-label">Prescribed By</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filters.prescribedBy}
                    onChange={(e) => handleFilterChange('prescribedBy', e.target.value)}
                    placeholder="Doctor name..."
                  />
                </div>

                {/* Date Range Filters */}
                <div className="mb-3">
                  <label className="form-label">Start Date From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Start Date To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>

                {/* Duration Filter */}
                <div className="mb-3">
                  <label className="form-label">Minimum Duration (days)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={filters.duration}
                    onChange={(e) => handleFilterChange('duration', e.target.value)}
                    placeholder="e.g., 30"
                    min="1"
                  />
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* View Medicine Offcanvas */}
      {showViewOffcanvas && selectedMedicine && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Medicine Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="row g-3">
                {/* Basic Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Basic Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-8">
                          <label className="form-label fw-bold">Medicine Name</label>
                          <p className="form-control-plaintext">
                            <FiActivity className="text-primary me-2" />
                            {selectedMedicine.name || 'Not specified'}
                          </p>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Duration</label>
                          <p className="form-control-plaintext">
                            <span className="badge bg-info">
                              {selectedMedicine.duration} days
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Status</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getMedicineStatusColor(selectedMedicine)}`}>
                              {getMedicineStatusText(selectedMedicine)}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Record Status</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getStatusColor(selectedMedicine.status)}`}>
                              {getStatusText(selectedMedicine.status)}
                            </span>
                          </p>
                        </div>
                        {selectedMedicine.comments && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Comments</label>
                            <p className="form-control-plaintext">
                              {selectedMedicine.comments}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Date Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiCalendar className="me-1" />
                            Start Date
                          </label>
                          <p className="form-control-plaintext">
                            {formatDate(selectedMedicine.start_date)}
                          </p>
                        </div>
                        {selectedMedicine.end_date && (
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              <FiCalendar className="me-1" />
                              End Date
                            </label>
                            <p className="form-control-plaintext">
                              {formatDate(selectedMedicine.end_date)}
                            </p>
                          </div>
                        )}
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Created</label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedMedicine.created_date)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Last Updated</label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedMedicine.updated_date)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Schedule Information */}
                {(selectedMedicine.usage_days_of_week?.length > 0 || selectedMedicine.usage_dates?.length > 0) && (
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">Schedule Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          {selectedMedicine.usage_days_of_week?.length > 0 && (
                            <div className="col-12">
                              <label className="form-label fw-bold">
                                <FiClock className="me-1" />
                                Days of Week
                              </label>
                              <p className="form-control-plaintext">
                                <div className="d-flex flex-wrap gap-1">
                                  {selectedMedicine.usage_days_of_week.map((day, index) => (
                                    <span key={index} className="badge bg-secondary">
                                      {getDayName(day)}
                                    </span>
                                  ))}
                                </div>
                              </p>
                            </div>
                          )}
                          {selectedMedicine.usage_dates?.length > 0 && (
                            <div className="col-12">
                              <label className="form-label fw-bold">Specific Dates</label>
                              <p className="form-control-plaintext">
                                <div className="d-flex flex-wrap gap-1">
                                  {selectedMedicine.usage_dates.map((date, index) => (
                                    <span key={index} className="badge bg-info">
                                      {formatDate(date)}
                                    </span>
                                  ))}
                                </div>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Personnel Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Personnel Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiUser className="me-1" />
                            Prescribed By
                          </label>
                          <p className="form-control-plaintext">
                            {selectedMedicine.prescribed_by || 'Unknown'}
                          </p>
                          {selectedMedicine.prescribed_by_email && (
                            <small className="text-muted">
                              {selectedMedicine.prescribed_by_email}
                            </small>
                          )}
                        </div>
                        {selectedMedicine.referred_by && selectedMedicine.referred_by !== 'Unknown' && (
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              <FiUser className="me-1" />
                              Referred By
                            </label>
                            <p className="form-control-plaintext">
                              {selectedMedicine.referred_by}
                            </p>
                            {selectedMedicine.referred_by_email && (
                              <small className="text-muted">
                                {selectedMedicine.referred_by_email}
                              </small>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="col-12">
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-outline-primary flex-fill"
                      onClick={() => {
                        handleCloseViewOffcanvas();
                        setTimeout(() => handleEditMedicine(selectedMedicine.medicine_id), 300);
                      }}
                    >
                      <FiEdit3 className="me-2" />
                      Edit Medicine
                    </button>
                    <button 
                      className="btn btn-outline-danger flex-fill"
                      onClick={() => handleDeleteMedicine(selectedMedicine.medicine_id)}
                    >
                      <FiTrash2 className="me-2" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add New Medicine Offcanvas */}
      {showAddOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseAddOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Add New Medicine</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseAddOffcanvas}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="offcanvas-body">
              <AddMedicineForm
  onSubmit={handleSubmitNewMedicine}
  onCancel={handleCloseAddOffcanvas}
  isSubmitting={isSubmitting}
  patientData={patientData}
  availableDoctors={availableDoctors}
  availableMedicines={availableMedicines}
  doctorsLoading={doctorsLoading}
  medicinesLoading={medicinesLoading}
/>
            </div>
          </div>
        </>
      )}

      {/* Edit Medicine Offcanvas */}
      {showEditOffcanvas && editingMedicine && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseEditOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Edit Medicine</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseEditOffcanvas}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="offcanvas-body">
              <EditMedicineForm
  medicine={editingMedicine}
  onSubmit={handleUpdateMedicine}
  onCancel={handleCloseEditOffcanvas}
  isSubmitting={isSubmitting}
  patientData={patientData}
  availableDoctors={availableDoctors}
  availableMedicines={availableMedicines}
  doctorsLoading={doctorsLoading}
  medicinesLoading={medicinesLoading}
/>
            </div>
          </div>
        </>
      )}

      {/* Patient Clinical Data Component */}
      <PatientMedicalClinicalData 
        patientData={patientData}
        alertInfo={alertInfo}
      />
    </main>
  );
};

// AddMedicineForm Component
const AddMedicineForm = ({ onSubmit, onCancel, isSubmitting, patientData, availableDoctors, availableMedicines, doctorsLoading, medicinesLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    selectedMedicineId: '', // Nuevo campo
    customMedicineName: '', // Nuevo campo
    duration: '',
    startdate: new Date().toISOString().split('T')[0],
    enddate: '',
    prescribedby: '',
    referredby: '',
    usagedaysofweek: [],
    usagedates: [],
    comments: ''
  });

  const [errors, setErrors] = useState({});
  const [useCustomMedicine, setUseCustomMedicine] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Lógica especial para medicina
    if (field === 'selectedMedicineId' && value) {
      const selectedMedicine = availableMedicines.find(med => med.id === value);
      if (selectedMedicine) {
        setFormData(prev => ({
          ...prev,
          name: selectedMedicine.name,
          customMedicineName: ''
        }));
        setUseCustomMedicine(false);
      }
    }
    
    if (field === 'customMedicineName') {
      setFormData(prev => ({
        ...prev,
        name: value,
        selectedMedicineId: ''
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      usagedaysofweek: prev.usagedaysofweek.includes(day)
        ? prev.usagedaysofweek.filter(d => d !== day)
        : [...prev.usagedaysofweek, day]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar medicina
    if (!useCustomMedicine && !formData.selectedMedicineId) {
      newErrors.selectedMedicineId = 'Please select a medicine or use custom name';
    }
    
    if (useCustomMedicine && !formData.customMedicineName.trim()) {
      newErrors.customMedicineName = 'Custom medicine name is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Medicine name is required';
    }

    if (!formData.duration || formData.duration < 1) {
      newErrors.duration = 'Duration must be at least 1 day';
    }

    if (!formData.startdate) {
      newErrors.startdate = 'Start date is required';
    }

    // Calculate end date if duration is provided
    if (formData.startdate && formData.duration) {
      const startDate = new Date(formData.startdate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + parseInt(formData.duration) - 1);
      
      const calculatedEndDate = endDate.toISOString().split('T')[0];
      if (formData.enddate && formData.enddate !== calculatedEndDate) {
        handleInputChange('enddate', calculatedEndDate);
      } else if (!formData.enddate) {
        handleInputChange('enddate', calculatedEndDate);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on success
      setFormData({
        name: '',
        selectedMedicineId: '',
        customMedicineName: '',
        duration: '',
        startdate: new Date().toISOString().split('T')[0],
        enddate: '',
        prescribedby: '',
        referredby: '',
        usagedaysofweek: [],
        usagedates: [],
        comments: ''
      });
      setUseCustomMedicine(false);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const getDayName = (dayNumber) => {
    const days = {
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
      '7': 'Sunday'
    };
    return days[dayNumber] || dayNumber;
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        {/* Patient Info */}
        <div className="col-12">
          <div className="alert alert-info">
            <FiUser className="me-2" />
            Adding medicine for: <strong>{patientData.patientName}</strong>
          </div>
        </div>

        {/* Medicine Selection */}
        <div className="col-12">
          <div className="d-flex align-items-center gap-3 mb-2">
            <label className="form-label required mb-0">Medicine *</label>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="useCustomMedicine"
                checked={useCustomMedicine}
                onChange={(e) => {
                  setUseCustomMedicine(e.target.checked);
                  if (e.target.checked) {
                    handleInputChange('selectedMedicineId', '');
                    handleInputChange('name', formData.customMedicineName);
                  } else {
                    handleInputChange('customMedicineName', '');
                    handleInputChange('name', '');
                  }
                }}
              />
              <label className="form-check-label" htmlFor="useCustomMedicine">
                Use custom medicine name
              </label>
            </div>
          </div>

          {!useCustomMedicine ? (
            <select
              className={`form-select ${errors.selectedMedicineId ? 'is-invalid' : ''}`}
              value={formData.selectedMedicineId}
              onChange={(e) => handleInputChange('selectedMedicineId', e.target.value)}
              disabled={isSubmitting || medicinesLoading}
            >
              <option value="">
                {medicinesLoading ? 'Loading medicines...' : 'Select a medicine'}
              </option>
              {availableMedicines.map(medicine => (
                <option key={medicine.id} value={medicine.id}>
                  {medicine.name}
                  {medicine.category ? ` (${medicine.category})` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              className={`form-control ${errors.customMedicineName ? 'is-invalid' : ''}`}
              value={formData.customMedicineName}
              onChange={(e) => handleInputChange('customMedicineName', e.target.value)}
              placeholder="Enter custom medicine name"
              disabled={isSubmitting}
            />
          )}
          
          {errors.selectedMedicineId && <div className="invalid-feedback">{errors.selectedMedicineId}</div>}
          {errors.customMedicineName && <div className="invalid-feedback">{errors.customMedicineName}</div>}
        </div>

        {/* Duration and Dates */}
        <div className="col-md-4">
          <label className="form-label required">Duration (days) *</label>
          <input
            type="number"
            className={`form-control ${errors.duration ? 'is-invalid' : ''}`}
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            min="1"
            placeholder="e.g., 7"
            disabled={isSubmitting}
          />
          {errors.duration && <div className="invalid-feedback">{errors.duration}</div>}
        </div>

        <div className="col-md-4">
          <label className="form-label required">Start Date *</label>
          <input
            type="date"
            className={`form-control ${errors.startdate ? 'is-invalid' : ''}`}
            value={formData.startdate}
            onChange={(e) => handleInputChange('startdate', e.target.value)}
            disabled={isSubmitting}
          />
          {errors.startdate && <div className="invalid-feedback">{errors.startdate}</div>}
        </div>

        <div className="col-md-4">
          <label className="form-label">End Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.enddate}
            onChange={(e) => handleInputChange('enddate', e.target.value)}
            disabled={isSubmitting}
            readOnly
          />
          <small className="text-muted">Calculated automatically from duration</small>
        </div>

        {/* Schedule - Days of Week */}
        <div className="col-12">
          <label className="form-label">Schedule - Days of Week</label>
          <div className="d-flex flex-wrap gap-2">
            {['1', '2', '3', '4', '5', '6', '7'].map(day => (
              <button
                key={day}
                type="button"
                className={`btn ${formData.usagedaysofweek.includes(day) ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleDayToggle(day)}
                disabled={isSubmitting}
              >
                {getDayName(day)}
              </button>
            ))}
          </div>
          <small className="text-muted">Select the days when this medicine should be taken</small>
        </div>

        {/* Personnel - Prescribed By */}
        <div className="col-md-6">
          <label className="form-label">Prescribed By</label>
          <select
            className="form-select"
            value={formData.prescribedby}
            onChange={(e) => handleInputChange('prescribedby', e.target.value)}
            disabled={isSubmitting || doctorsLoading}
          >
            <option value="">
              {doctorsLoading ? 'Loading doctors...' : 'Select prescribing doctor (optional)'}
            </option>
            {availableDoctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
                {doctor.email ? ` (${doctor.email})` : ''}
              </option>
            ))}
          </select>
          <small className="text-muted">Leave blank to use current user</small>
        </div>

        {/* Personnel - Referred By */}
        <div className="col-md-6">
          <label className="form-label">Referred By</label>
          <select
            className="form-select"
            value={formData.referredby}
            onChange={(e) => handleInputChange('referredby', e.target.value)}
            disabled={isSubmitting || doctorsLoading}
          >
            <option value="">Select referring doctor (optional)</option>
            {availableDoctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
                {doctor.email ? ` (${doctor.email})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Comments */}
        <div className="col-12">
          <label className="form-label">Comments</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.comments}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            placeholder="Dosage instructions, special notes, etc."
            disabled={isSubmitting}
          />
        </div>

        {/* Action Buttons */}
        <div className="col-12">
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <FiCheck className="me-2" />
                  Create Medicine
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

// EditMedicineForm Component
const EditMedicineForm = ({ onSubmit, onCancel, isSubmitting, patientData, availableDoctors, availableMedicines, doctorsLoading, medicinesLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    selectedMedicineId: '', // Nuevo campo
    customMedicineName: '', // Nuevo campo
    duration: '',
    startdate: new Date().toISOString().split('T')[0],
    enddate: '',
    prescribedby: '',
    referredby: '',
    usagedaysofweek: [],
    usagedates: [],
    comments: ''
  });

  const [errors, setErrors] = useState({});
  const [useCustomMedicine, setUseCustomMedicine] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Lógica especial para medicina
    if (field === 'selectedMedicineId' && value) {
      const selectedMedicine = availableMedicines.find(med => med.id === value);
      if (selectedMedicine) {
        setFormData(prev => ({
          ...prev,
          name: selectedMedicine.name,
          customMedicineName: ''
        }));
        setUseCustomMedicine(false);
      }
    }
    
    if (field === 'customMedicineName') {
      setFormData(prev => ({
        ...prev,
        name: value,
        selectedMedicineId: ''
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      usagedaysofweek: prev.usagedaysofweek.includes(day)
        ? prev.usagedaysofweek.filter(d => d !== day)
        : [...prev.usagedaysofweek, day]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar medicina
    if (!useCustomMedicine && !formData.selectedMedicineId) {
      newErrors.selectedMedicineId = 'Please select a medicine or use custom name';
    }
    
    if (useCustomMedicine && !formData.customMedicineName.trim()) {
      newErrors.customMedicineName = 'Custom medicine name is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Medicine name is required';
    }

    if (!formData.duration || formData.duration < 1) {
      newErrors.duration = 'Duration must be at least 1 day';
    }

    if (!formData.startdate) {
      newErrors.startdate = 'Start date is required';
    }

    // Calculate end date if duration is provided
    if (formData.startdate && formData.duration) {
      const startDate = new Date(formData.startdate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + parseInt(formData.duration) - 1);
      
      const calculatedEndDate = endDate.toISOString().split('T')[0];
      if (formData.enddate && formData.enddate !== calculatedEndDate) {
        handleInputChange('enddate', calculatedEndDate);
      } else if (!formData.enddate) {
        handleInputChange('enddate', calculatedEndDate);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on success
      setFormData({
        name: '',
        selectedMedicineId: '',
        customMedicineName: '',
        duration: '',
        startdate: new Date().toISOString().split('T')[0],
        enddate: '',
        prescribedby: '',
        referredby: '',
        usagedaysofweek: [],
        usagedates: [],
        comments: ''
      });
      setUseCustomMedicine(false);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const getDayName = (dayNumber) => {
    const days = {
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
      '7': 'Sunday'
    };
    return days[dayNumber] || dayNumber;
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        {/* Patient Info */}
        <div className="col-12">
          <div className="alert alert-info">
            <FiUser className="me-2" />
            Adding medicine for: <strong>{patientData.patientName}</strong>
          </div>
        </div>

        {/* Medicine Selection */}
        <div className="col-12">
          <div className="d-flex align-items-center gap-3 mb-2">
            <label className="form-label required mb-0">Medicine *</label>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="useCustomMedicine"
                checked={useCustomMedicine}
                onChange={(e) => {
                  setUseCustomMedicine(e.target.checked);
                  if (e.target.checked) {
                    handleInputChange('selectedMedicineId', '');
                    handleInputChange('name', formData.customMedicineName);
                  } else {
                    handleInputChange('customMedicineName', '');
                    handleInputChange('name', '');
                  }
                }}
              />
              <label className="form-check-label" htmlFor="useCustomMedicine">
                Use custom medicine name
              </label>
            </div>
          </div>

          {!useCustomMedicine ? (
            <select
              className={`form-select ${errors.selectedMedicineId ? 'is-invalid' : ''}`}
              value={formData.selectedMedicineId}
              onChange={(e) => handleInputChange('selectedMedicineId', e.target.value)}
              disabled={isSubmitting || medicinesLoading}
            >
              <option value="">
                {medicinesLoading ? 'Loading medicines...' : 'Select a medicine'}
              </option>
              {availableMedicines.map(medicine => (
                <option key={medicine.id} value={medicine.id}>
                  {medicine.name}
                  {medicine.category ? ` (${medicine.category})` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              className={`form-control ${errors.customMedicineName ? 'is-invalid' : ''}`}
              value={formData.customMedicineName}
              onChange={(e) => handleInputChange('customMedicineName', e.target.value)}
              placeholder="Enter custom medicine name"
              disabled={isSubmitting}
            />
          )}
          
          {errors.selectedMedicineId && <div className="invalid-feedback">{errors.selectedMedicineId}</div>}
          {errors.customMedicineName && <div className="invalid-feedback">{errors.customMedicineName}</div>}
        </div>

        {/* Duration and Dates */}
        <div className="col-md-4">
          <label className="form-label required">Duration (days) *</label>
          <input
            type="number"
            className={`form-control ${errors.duration ? 'is-invalid' : ''}`}
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            min="1"
            placeholder="e.g., 7"
            disabled={isSubmitting}
          />
          {errors.duration && <div className="invalid-feedback">{errors.duration}</div>}
        </div>

        <div className="col-md-4">
          <label className="form-label required">Start Date *</label>
          <input
            type="date"
            className={`form-control ${errors.startdate ? 'is-invalid' : ''}`}
            value={formData.startdate}
            onChange={(e) => handleInputChange('startdate', e.target.value)}
            disabled={isSubmitting}
          />
          {errors.startdate && <div className="invalid-feedback">{errors.startdate}</div>}
        </div>

        <div className="col-md-4">
          <label className="form-label">End Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.enddate}
            onChange={(e) => handleInputChange('enddate', e.target.value)}
            disabled={isSubmitting}
            readOnly
          />
          <small className="text-muted">Calculated automatically from duration</small>
        </div>

        {/* Schedule - Days of Week */}
        <div className="col-12">
          <label className="form-label">Schedule - Days of Week</label>
          <div className="d-flex flex-wrap gap-2">
            {['1', '2', '3', '4', '5', '6', '7'].map(day => (
              <button
                key={day}
                type="button"
                className={`btn ${formData.usagedaysofweek.includes(day) ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleDayToggle(day)}
                disabled={isSubmitting}
              >
                {getDayName(day)}
              </button>
            ))}
          </div>
          <small className="text-muted">Select the days when this medicine should be taken</small>
        </div>

        {/* Personnel - Prescribed By */}
        <div className="col-md-6">
          <label className="form-label">Prescribed By</label>
          <select
            className="form-select"
            value={formData.prescribedby}
            onChange={(e) => handleInputChange('prescribedby', e.target.value)}
            disabled={isSubmitting || doctorsLoading}
          >
            <option value="">
              {doctorsLoading ? 'Loading doctors...' : 'Select prescribing doctor (optional)'}
            </option>
            {availableDoctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
                {doctor.email ? ` (${doctor.email})` : ''}
              </option>
            ))}
          </select>
          <small className="text-muted">Leave blank to use current user</small>
        </div>

        {/* Personnel - Referred By */}
        <div className="col-md-6">
          <label className="form-label">Referred By</label>
          <select
            className="form-select"
            value={formData.referredby}
            onChange={(e) => handleInputChange('referredby', e.target.value)}
            disabled={isSubmitting || doctorsLoading}
          >
            <option value="">Select referring doctor (optional)</option>
            {availableDoctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
                {doctor.email ? ` (${doctor.email})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Comments */}
        <div className="col-12">
          <label className="form-label">Comments</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.comments}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            placeholder="Dosage instructions, special notes, etc."
            disabled={isSubmitting}
          />
        </div>

        {/* Action Buttons */}
        <div className="col-12">
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Updating...
                </>
              ) : (
                <>
                  <FiCheck className="me-2" />
                  Update Medicine
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MedicineTreatment;